/* GASSISA
=========================================================================================
* Descripción: Menu Actions para Toma de Inventarios
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 29/01/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 06/04/2026 | Lic. José Luis Aragón C.         | Adaptación para toma inventario
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
*      4       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminaciones en submenú de otras acciones
*      5       | 12/08/2026 | Lic. José Luis Aragón Cervantes  | Botón visible para cancelar toma sin incrementar folio
* ---------------------------------------------------------------------------------------
*/

import { IconButton, Tooltip } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import UploadIcon from "@mui/icons-material/Upload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import LockIcon from "@mui/icons-material/Lock";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ReplayIcon from "@mui/icons-material/Replay";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import { Fragment } from "react";
import { useInventarios } from "../../../context/Inventarios/InventariosContext";
import { useDoubleClickPrevention } from "../../../hooks/useDoubleClickPrevention";
import MnuDangerActionsMenu from "../../utils/MnuDangerActionsMenu";

export default function MnuTomaInventarioActions({
  onAccion,
  tieneArticulos = false,
  rowSelected = null,
  onEliminarArticulo = null,
  onEliminarTodos = null,
  onEditarConteo3 = null,
}) {
  const {
    handleClickRegresarMainDashboard,
    handleTomaCrearFolio,
    handleTomaGuardarHeader,
    handleTomaCargarArticulos,
    handleTomaLoadAll,
    handleTomaCargarActivo,
    handleTomaBuscarArticulos,
    handleTomaImportarConteos,
    handleTomaValidarZonas,
    handleTomaFijarConteoPorRegla,
    handleTomaFijarTodoConteo,
    handleTomaReiniciarConteos,
    handleTomaReiniciarConteoArticulo,
    handleTomaEliminarConteosEscaneo,
    handleTomaImportarConteosAConteosEscaneo,
    handleTomaCerrarInventario,
    handleTomaAplicarInventario,
    handleTomaCancelarInventario,
    handleTomaImprimirPdf,
    handleArticuloSeleccionado,
    tomaHeader,
  } = useInventarios();
  const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

  const hasFolio = tomaHeader?.folioinventario > 0;
  const canEdit = tomaHeader?.statusinventario === "AB";
  const isGeneral = tomaHeader?.tipoinventario === "GENERAL";

  const isNuevo = !hasFolio;
  const tieneTipo = !!tomaHeader?.tipoinventario;
  const tieneResponsable = !!(tomaHeader?.usuarioresponsable || "").trim();
  const tieneAlmacen = !!(tomaHeader?.almacenexhibidor || "").trim();
  const noHayInventarioActivo =
    !hasFolio || (tomaHeader?.statusinventario !== "PE" && tomaHeader?.statusinventario !== "AB");
  const puedeIniciar =
    isNuevo &&
    tieneTipo &&
    tieneResponsable &&
    tieneAlmacen &&
    noHayInventarioActivo;

  const handleCrearFolio = async () => {
    if (onAccion) onAccion("crearFolio");
    else await handleTomaCrearFolio();
  };

  const handleGuardar = async () => {
    if (onAccion) onAccion("guardar");
    else await handleTomaGuardarHeader(tomaHeader);
  };

  const handleRecargarActivo = async () => {
    if (onAccion) onAccion("recargarActivo");
    else await handleTomaCargarActivo();
  };

  const handleRefrescar = async () => {
    if (onAccion) onAccion("refrescar");
    else if (hasFolio) await handleTomaLoadAll(tomaHeader.folioinventario);
    else await handleTomaCargarActivo();
  };

  const handleCargarArticulos = async () => {
    if (onAccion) onAccion("cargarArticulos");
    else await handleTomaCargarArticulos();
  };

  const handleBuscarArticulo = () => {
    if (onAccion) onAccion("buscarArticulo");
    else handleTomaBuscarArticulos();
  };

  const handleImportar = async () => {
    if (onAccion) onAccion("importar");
    else await handleTomaImportarConteos();
  };

  const handleValidar = async () => {
    if (onAccion) onAccion("validar");
    else await handleTomaValidarZonas();
  };

  const handleAplicarCerrar = async () => {
    if (onAccion) onAccion("aplicarCerrar");
    else {
      await handleTomaCerrarInventario();
      await handleTomaAplicarInventario(undefined, true);
    }
  };

  const handleCancelarToma = async () => {
    if (onAccion) onAccion("cancelarToma");
    else await handleTomaCancelarInventario(tomaHeader?.folioinventario);
  };

  const handleImprimir = async () => {
    if (onAccion) onAccion("imprimir");
    else await handleTomaImprimirPdf();
  };

  const handleFijarTodo1 = async () => {
    if (onAccion) onAccion("fijarTodo1");
    else await handleTomaFijarTodoConteo(1);
  };

  const handleFijarTodo2 = async () => {
    if (onAccion) onAccion("fijarTodo2");
    else await handleTomaFijarTodoConteo(2);
  };

  const handleFijarTodo3 = async () => {
    if (onAccion) onAccion("fijarTodo3");
    else await handleTomaFijarTodoConteo(3);
  };

  const handleReiniciar = async () => {
    if (onAccion) onAccion("reiniciar");
    else await handleTomaReiniciarConteos();
  };

  const handleImportarConteosAConteosEscaneo = async () => {
    await handleTomaImportarConteosAConteosEscaneo();
  };

  const handleEliminarConteosEscaneo = async () => {
    await handleTomaEliminarConteosEscaneo();
  };

  const handleEliminarArticulo = () => {
    if (onEliminarArticulo) onEliminarArticulo();
  };

  const handleEliminarTodos = () => {
    if (onEliminarTodos) onEliminarTodos();
  };

  const handleReiniciarArticulo = () => {
    if (tieneArticuloSeleccionado) {
      handleTomaReiniciarConteoArticulo(rowSelected.clavearticulo);
    }
  };

  const handleEditarConteo3 = () => {
    if (onEditarConteo3) onEditarConteo3();
  };

  const tieneArticuloSeleccionado = rowSelected && rowSelected.clavearticulo;

  return (
    <Fragment>
      <Tooltip title={"Home"}>
        <span>
          <IconButton onClick={handleClickRegresarMainDashboard}>
            <HomeIcon className="icon" />
            <p className="icon">Salir</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Refrescar toma de inventario">
        <span>
          <IconButton onClick={() => executeWithProtection(handleRefrescar)} disabled={isProcessing}>
            <RefreshIcon className="icon" />
            <p className="icon">Refrescar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Recargar inventario activo">
        <span>
          <IconButton onClick={() => executeWithProtection(handleRecargarActivo)} disabled={isProcessing}>
            <AutorenewIcon className="icon" />
            <p className="icon">Recargar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={
          puedeIniciar
            ? "Iniciar inventario"
            : !noHayInventarioActivo
            ? "Ya hay un inventario activo"
            : tieneArticulos
            ? "Ya tiene artículos cargados"
            : "Complete tipo, responsable y almacén"
        }
      >
        <span>
          <IconButton onClick={() => executeWithProtection(handleCrearFolio)} disabled={isProcessing || !puedeIniciar}>
            <PlayArrowIcon className="icon" />
            <p className="icon">Iniciar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Guardar">
        <span>
          <IconButton onClick={() => executeWithProtection(handleGuardar)} disabled={isProcessing || !hasFolio || !canEdit}>
            <SaveIcon className="icon" />
            <p className="icon">Guardar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Importar artículos">
        <span>
          <IconButton
            onClick={() => executeWithProtection(handleCargarArticulos)}
            disabled={isProcessing || !hasFolio || !canEdit}
          >
            <UploadIcon className="icon" />
            <p className="icon">Importar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Importar conteos (C1/C2/C3)">
        <span>
          <IconButton onClick={() => executeWithProtection(handleImportar)} disabled={isProcessing || !hasFolio || !canEdit}>
            <CloudDownloadIcon className="icon" />
            <p className="icon">Conteos</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Validar zonas">
        <span>
          <IconButton onClick={() => executeWithProtection(handleValidar)} disabled={isProcessing || !hasFolio || !canEdit}>
            <CheckCircleIcon className="icon" />
            <p className="icon">Validar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Aplicar inventario al almacén">
        <span>
          <IconButton
            onClick={() => executeWithProtection(handleAplicarCerrar)}
            disabled={
              isProcessing ||
              !hasFolio ||
              !["AB", "CE"].includes(tomaHeader?.statusinventario)
            }
          >
            <CheckCircleIcon className="icon" />
            <p className="icon">Aplicar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Cancelar inventario sin incrementar folio">
        <span>
          <IconButton
            onClick={() => executeWithProtection(handleCancelarToma)}
            disabled={
              isProcessing ||
              !hasFolio ||
              !["AB", "CE"].includes(tomaHeader?.statusinventario)
            }
          >
            <CancelIcon className="icon" />
            <p className="icon">Cancelar</p>
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Imprimir PDF">
        <span>
          <IconButton onClick={() => executeWithProtection(handleImprimir)} disabled={isProcessing || !hasFolio}>
            <PrintIcon className="icon" />
            <p className="icon">Imprimir</p>
          </IconButton>
        </span>
      </Tooltip>

      <MnuDangerActionsMenu
        tooltip="Otras acciones"
        actions={[
          {
            label: "Fijar todo a conteo 1",
            icon: LockOpenIcon,
            onClick: handleFijarTodo1,
            disabled: !hasFolio || !canEdit || !tieneArticulos,
          },
          {
            label: "Fijar todo a conteo 2",
            icon: LockOpenIcon,
            onClick: handleFijarTodo2,
            disabled: !hasFolio || !canEdit || !tieneArticulos,
          },
          {
            label: "Fijar todo a conteo 3",
            icon: LockOpenIcon,
            onClick: handleFijarTodo3,
            disabled: !hasFolio || !canEdit || !tieneArticulos,
          },
          {
            label: "Reiniciar conteos",
            icon: ReplayIcon,
            onClick: handleReiniciar,
            disabled: !hasFolio || !canEdit || !tieneArticulos,
          },
          {
            label: "Importar Conteos → Escaneos",
            icon: UploadIcon,
            onClick: handleImportarConteosAConteosEscaneo,
            disabled: !hasFolio || !canEdit,
          },
          {
            label: "Limpiar ConteosEscaneo (terminales)",
            icon: DeleteSweepIcon,
            onClick: handleEliminarConteosEscaneo,
            disabled: !hasFolio,
          },
          {
            label: "Reiniciar conteo (seleccionado)",
            icon: ReplayIcon,
            onClick: handleReiniciarArticulo,
            disabled: !hasFolio || !canEdit || !tieneArticuloSeleccionado,
          },
          {
            label: "Editar conteo 3 (seleccionado)",
            icon: EditIcon,
            onClick: handleEditarConteo3,
            disabled: !hasFolio || !canEdit || !tieneArticuloSeleccionado,
          },
          {
            label: "Eliminar artículo seleccionado",
            icon: DeleteIcon,
            onClick: handleEliminarArticulo,
            disabled: !hasFolio || !canEdit || !tieneArticuloSeleccionado,
          },
          {
            label: "Eliminar todos los artículos",
            icon: DeleteSweepIcon,
            onClick: handleEliminarTodos,
            disabled: !hasFolio || !canEdit || !tieneArticulos,
          },
        ]}
      />
    </Fragment>
  );
}
